import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { SectionHeading, tone } from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { useLandingT, usePageVars } from './kit';
import type { LandingCopy } from './pages';

/** The page's three steps, written for its kind of file or recording. */
export default function HowItWorks({ page }: { page: LandingCopy }) {
  const { t } = useLandingT();
  const vars = usePageVars(page);
  const reveal = useReveal<HTMLDivElement>();

  return (
    <section id='how-it-works' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('From recording to text')}
          tail={t('in three steps.')}
          lead={t(
            'No software to install. It works in your browser on any computer or phone.',
          )}
        />

        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-14 grid gap-10 sm:grid-cols-3 sm:gap-0',
          )}
        >
          {page.steps.map((step, index) => (
            <div
              key={step.title}
              className={clsxm(
                styles.rv,
                'sm:px-8 sm:first:ps-0 sm:last:pe-0',
                index > 0 &&
                  'sm:border-s sm:border-[#0f0f28]/[0.08] dark:sm:border-white/[0.08]',
              )}
              style={{ '--d': `${index * 120}ms` } as React.CSSProperties}
            >
              <p className='font-mono text-sm font-semibold text-primary-600 dark:text-primary-400'>
                {String(index + 1).padStart(2, '0')}
              </p>
              <h3
                className={clsxm(
                  'mt-4 text-xl font-semibold tracking-tight',
                  tone.ink,
                )}
              >
                {t(step.title, vars)}
              </h3>
              <p className={clsxm('mt-3 text-[16px] leading-7', tone.body)}>
                {t(step.body, vars)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
